import { z } from "zod";

import {
  ActivationStatus,
  ActivationStatusSchema,
  ActivationTypeSchema,
  DocumentRefType,
  DocumentRefTypeSchema,
  DocumentTypeSchema,
  ReferralRewardStatusSchema
} from "../models/generated";

/* ------------------------------------------------------------- */
/* Account management                                             */
/* ------------------------------------------------------------- */

export const AccountListItemSchema = z.object({
  id: z.string(),
  referralCode: z.string(),
  userId: z.string().nullable(),
  parentId: z.string().nullable(),
  createdAt: z.coerce.date(),
  user: z
    .object({
      name: z.string(),
      email: z.string(),
      avatar: z.string().nullable()
    })
    .nullable(),
  org: z
    .object({
      name: z.string()
    })
    .nullable(),
  activations: z.array(z.object({ type: ActivationTypeSchema, status: ActivationStatusSchema })).nullish()
});

export type AccountListItem = z.infer<typeof AccountListItemSchema>;

export const AdminAccountDetailSchema = z.object({
  id: z.string(),
  referralCode: z.string(),
  parentId: z.string().nullable(),
  createdAt: z.coerce.date(),
  user: z
    .object({
      name: z.string(),
      email: z.string(),
      avatar: z.string().nullable()
    })
    .nullable(),
  org: z
    .object({
      name: z.string()
    })
    .nullable(),
  activations: z.array(
    z.object({
      type: ActivationTypeSchema,
      status: ActivationStatusSchema,
      createdAt: z.coerce.date()
    })
  )
});

export type AdminAccountDetail = z.infer<typeof AdminAccountDetailSchema>;

export const ListAccountsQuerySchema = z.object({
  page: z.coerce.number().min(0).default(0),
  limit: z.coerce.number().min(1).default(20),
  activationType: ActivationTypeSchema.optional(),
  activationStatus: ActivationStatusSchema.optional(),
  search: z.string().optional()
});
export type ListAccountsQuery = z.infer<typeof ListAccountsQuerySchema>;

/* ------------------------------------------------------------- */
/* Application reward management (replaces Commission)           */
/* ------------------------------------------------------------- */

export const ListAdminCommissionsQuerySchema = z.object({
  page: z.coerce.number().min(0).default(0),
  limit: z.coerce.number().min(1).default(20),
  status: ReferralRewardStatusSchema.optional(),
  agentId: z.string().optional()
});
export type ListAdminCommissionsQuery = z.infer<typeof ListAdminCommissionsQuerySchema>;

export const ApplicationRewardWithDetailSchema = z.object({
  id: z.string(),
  accountId: z.string(),
  applicationId: z.string(),
  creditTxId: z.string().nullable(),
  amount: z.number(),
  level: z.number(),
  status: ReferralRewardStatusSchema,
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  account: z
    .object({
      id: z.string(),
      user: z
        .object({
          name: z.string().nullable(),
          email: z.string().nullable()
        })
        .nullable()
    })
    .optional()
});
export type ApplicationRewardWithDetail = z.infer<typeof ApplicationRewardWithDetailSchema>;

/* ------------------------------------------------------------- */
/* Reports                                                        */
/* ------------------------------------------------------------- */

export const RevenueReportQuerySchema = z.object({
  from: z.coerce.date(),
  to: z.coerce.date()
});
export type RevenueReportQuery = z.infer<typeof RevenueReportQuerySchema>;

export const RevenueDayEntrySchema = z.object({
  date: z.string(),
  amount: z.number()
});
export type RevenueDayEntry = z.infer<typeof RevenueDayEntrySchema>;

export const RevenueReportSchema = z.object({
  depositRevenue: z.number(),
  depositRevenueByDay: z.array(RevenueDayEntrySchema),
  activationRevenue: z.number(),
  activationRevenueByAccountType: z.record(z.string(), z.number())
});
export type RevenueReport = z.infer<typeof RevenueReportSchema>;

export const ConversionReportSchema = z.object({
  totalAccounts: z.number(),
  activeAccounts: z.number(),
  pendingApplications: z.number(),
  acceptedApplications: z.number(),
  pendingCommissions: z.number(),
  livePrograms: z.number(),
  byType: z.object({
    AGENT: z.number(),
    CANDIDATE: z.number(),
    ORG: z.number()
  })
});
export type ConversionReport = z.infer<typeof ConversionReportSchema>;

export const AgentPerformanceItemSchema = z.object({
  agentId: z.string(),
  agentName: z.string().nullable(),
  agentEmail: z.string().nullable(),
  candidateCount: z.number(),
  applicationCount: z.number(),
  acceptedCount: z.number(),
  totalCommissions: z.number()
});
export type AgentPerformanceItem = z.infer<typeof AgentPerformanceItemSchema>;

export const AgentPerformanceReportSchema = z.object({
  agents: z.array(AgentPerformanceItemSchema),
  meta: z.object({
    page: z.number(),
    limit: z.number(),
    total: z.number()
  })
});
export type AgentPerformanceReport = z.infer<typeof AgentPerformanceReportSchema>;

export const AgentPerformanceQuerySchema = z.object({
  page: z.coerce.number().min(0).default(0),
  limit: z.coerce.number().min(1).default(20)
});
export type AgentPerformanceQuery = z.infer<typeof AgentPerformanceQuerySchema>;

/* ------------------------------------------------------------- */
/* Activation status mutation                                     */
/* ------------------------------------------------------------- */

export const AccountActivationStatusValues = {
  SUSPENDED: "SUSPENDED",
  ACTIVE: "ACTIVE"
} as const;

export const ADMIN_ACTIVATION_STATUSES = [ActivationStatus.ACTIVE, ActivationStatus.SUSPENDED] as const;

/* ------------------------------------------------------------- */
/* Account deletion                                               */
/* ------------------------------------------------------------- */

export const DeleteAccountByEmailSchema = z.object({
  email: z.string().email()
});
export type DeleteAccountByEmailRequest = z.infer<typeof DeleteAccountByEmailSchema>;

/* ------------------------------------------------------------- */
/* Document management                                            */
/* ------------------------------------------------------------- */

export const DocumentListItemSchema = z.object({
  id: z.string(),
  refType: DocumentRefTypeSchema,
  refId: z.string(),
  type: DocumentTypeSchema,
  content: z.any(),
  notes: z.string().array(),
  verified: z.boolean(),
  bgId: z.string().nullable(),
  verifiedAt: z.coerce.date().nullable(),
  verifiedById: z.string().nullable(),
  createdAt: z.coerce.date()
});
export type DocumentListItem = z.infer<typeof DocumentListItemSchema>;

export const ListDocumentsQuerySchema = z.object({
  page: z.coerce.number().min(0).default(0),
  limit: z.coerce.number().min(1).default(20),
  refType: DocumentRefTypeSchema.optional(),
  refId: z.string().optional(),
  type: DocumentTypeSchema.optional(),
  verified: z.coerce.boolean().optional()
});
export type ListDocumentsQuery = z.infer<typeof ListDocumentsQuerySchema>;

export const CreateAdminDocumentSchema = z.object({
  refType: DocumentRefTypeSchema.default(DocumentRefType.USER),
  refId: z.string().min(1),
  type: DocumentTypeSchema,
  content: z.record(z.string(), z.any()).optional().default({}),
  notes: z.string().array().optional().default([]),
  bgId: z.string().min(1)
});
export type CreateAdminDocument = z.infer<typeof CreateAdminDocumentSchema>;

export const VerifyAdminDocumentSchema = z.object({
  bgId: z.string().min(1)
});
export type VerifyAdminDocument = z.infer<typeof VerifyAdminDocumentSchema>;
